import { Multimap } from "@flakiness/shared/common/multimap.js";

export class Operator<T> {
  evaluate: (left: T, value: T) => boolean;
  highlight: (left: T, value: T, hl: Multimap<T, [number, number]>) => void;

  constructor(options: {
    evaluate: (left: T, value: T) => boolean,
    highlight: (left: T, value: T, hl: Multimap<T, [number, number]>) => void,
  }) {
    this.evaluate = options.evaluate;
    this.highlight = options.highlight;
  }

  isEqual(other: Operator<any>) {
    // Operators are never created dynamically, so we can compare them by identity.
    return this === other;
  }

  static numberOperators = {
    EQ: new Operator<number>({
      evaluate: (left, value) => Math.abs(left - value) < 1e-3,
      highlight: (left, value, hl) => hl.set(left, [-Infinity, Infinity]),
    }),
    LT: new Operator<number>({
      evaluate: (left, value) => left < value,
      highlight: (left, value, hl) => hl.set(left, [-Infinity, Infinity]),
    }),
    LTE: new Operator<number>({
      evaluate: (left, value) => left <= value,
      highlight: (left, value, hl) => hl.set(left, [-Infinity, Infinity]),
    }),
    GT: new Operator<number>({
      evaluate: (left, value) => left > value,
      highlight: (left, value, hl) => hl.set(left, [-Infinity, Infinity]),
    }),
    GTE: new Operator<number>({
      evaluate: (left, value) => left >= value,
      highlight: (left, value, hl) => hl.set(left, [-Infinity, Infinity]),
    }),
  } satisfies Record<string, Operator<number>>;

  static stringOperators = {
    CONTAINS: new Operator<string>({
      evaluate: (left, value) => left.toLowerCase().indexOf(value.toLowerCase()) !== -1,
      highlight: (left, needle, hl) => highlightSubstrings(hl, needle.toLowerCase(), [left]),  
    }),
    EQ: new Operator<string>({
      evaluate: (left, value) => value.toLowerCase() === left.toLowerCase(),
      highlight: (left, value, hl) => {
        if (value.length)
          hl.set(left, [0, value.length - 1]);
      },
    }),
    NEQ: new Operator<string>({
      evaluate: (left, value) => value.toLowerCase() !== left.toLowerCase(),
      highlight: (left, value, hl) => {},
    }),
  } satisfies Record<string, Operator<string>>;
}

function highlightSubstrings(hl: Multimap<string, [number, number]>, needle: string, tokens: (string|undefined)[]) {
  if (!needle.length)
    return;
  for (const text of tokens) {
    if (!text)
      continue;
    const i = text.toLowerCase().indexOf(needle);
    if (i !== -1)
      hl.set(text, [i, i + needle.length - 1])
  }
}